import React, { useEffect, useState } from 'react'
import {
  Button,
  Dialog,
  SingleSelectionGroup,
  HeadingText,
} from '@paypalcorp/pp-react'
import clientLogger from 'ppbl/utils/client-logger'
import { getLocalizedText } from 'ppbl/utils/locale-helper'
import { AddressType } from '../../types'
import { useApplicationContext } from 'ppbl/hooks'
import { trackClick, trackPage } from 'ppbl/utils/fpti-helper'
import { addressDialogConstants, addressStatus } from 'ppbl/utils/constants'
import { merge } from 'lodash-es'
import serverData from 'ppbl/utils/server-data'
import { WorldReady, AddressFormat } from '@paypalcorp/worldready'
import './index.css'

const fptiData = {
  page: 'addressNormalizationModal',
}

type Props = {
  isAddressDialogOpen: boolean
  setIsAddressDialogOpen: (boolean) => void
  confirmAddress?: (string) => void
  newAddress?: () => void
  status: string
  suggestedAddress?: {
    status: string
    address: AddressType
  }
  userAddress: AddressType
  applicationId: string
}

const AddressConfirmationDialog = ({
  isAddressDialogOpen,
  setIsAddressDialogOpen,
  confirmAddress,
  newAddress,
  status,
  suggestedAddress,
  userAddress,
  applicationId,
}: Props) => {
  const { data } = useApplicationContext()
  const [isRecommendedAddress, setIsRecommendedAddress] =
    useState<boolean>(true)

  useEffect(() => {
    if (isAddressDialogOpen) {
      trackPage(
        merge(fptiData, {
          applicationId,
        }),
      )
      clientLogger.info(
        'Address Normalization Modal is opened - Application ID:',
        data?.applicationMetaData?.applicationId,
      )
    }
  }, [isAddressDialogOpen])

  const handleDialogClose = (isAddnewAddressClicked) => {
    if (isAddnewAddressClicked === 'addNewAddress') {
      trackClick(
        merge(fptiData, {
          component: 'closeAddressNormalizationDialog_addNewAddress_button',
          link: 'closeAddressNormalizationDialog_addNewAddress_button',
          applicationId,
        }),
      )
      clientLogger.info(
        'Address Confirmation prompt closes on add new address button click',
      )
    } else {
      trackClick(
        merge(fptiData, {
          component: 'closeAddressNormalizationDialog_close_button',
          link: 'closeAddressNormalizationDialog_close_button',
          applicationId,
        }),
      )
      clientLogger.info('Address Confirmation prompt closes')
    }
    setIsAddressDialogOpen(false)
  }

  const handleConfirmAddress = () => {
    if (isRecommendedAddress && status === addressStatus.CORRECTED) {
      trackClick(
        merge(fptiData, {
          component: 'recommended_address_selected',
          link: 'recommended_address_selected',
          applicationId,
        }),
      )
      clientLogger.info('AMS address Confirmation select recomended address')
      confirmAddress(addressDialogConstants.recommendedAddress)
    } else {
      trackClick(
        merge(fptiData, {
          component: 'userEntered_address_selected',
          link: 'userEntered_address_selected',
          applicationId,
        }),
      )
      clientLogger.info('AMS address Confirmation select user entered address')
      confirmAddress(addressDialogConstants.enteredAddress)
    }
  }

  const formatAddress = (address) => {
    const worldReadyLocale = serverData.locality.locale
    const worldReady = new WorldReady(worldReadyLocale)
    const addressFormatter = new AddressFormat(worldReady, {
      addressRegion: 'US',
    })
    return addressFormatter.format({
      addressLine1: address?.addressLine1,
      addressLine2: address?.addressLine2 ? address?.addressLine2 : '',
      adminArea1: address?.adminArea1,
      adminArea2: address?.adminArea2,
      postalCode: address?.postalCode,
      countryCode: address?.countryCode,
    })
  }

  return (
    <Dialog
      isOpen={isAddressDialogOpen}
      onClose={handleDialogClose}
      mobileFixedHeight="dynamic"
      showTitleInHeader={true}
      title={
        <img
          src="https://www.paypalobjects.com/paypal-ui/logos/svg/paypal-mark-color.svg"
          alt="paypal-logo"
          style={{ width: '32px' }}
        />
      }
      footerContents=""
    >
      <div className="wrapper">
        {status === addressStatus.CORRECTED ? (
          <>
            <div className="headingWrapperams">
              <HeadingText size="sm">
                {getLocalizedText(
                  'components.addressConfirmationDialog.correctedHeadingText',
                )}
              </HeadingText>
            </div>
            <div className="selectorWrapper">
              <SingleSelectionGroup
                name="address"
                label={getLocalizedText(
                  'components.addressConfirmationDialog.recomendedAddress',
                )}
                align="vertical"
                secondary
                alignContent="left"
                value="val1"
                onClick={() => setIsRecommendedAddress(true)}
                options={[
                  {
                    label: '',
                    secondaryLabel: formatAddress(suggestedAddress?.address),
                    value: 'val1',
                  },
                ]}
              />
              <SingleSelectionGroup
                name="address"
                label={getLocalizedText(
                  'components.addressConfirmationDialog.enteredAddress',
                )}
                align="vertical"
                secondary
                alignContent="left"
                value="val1"
                onClick={() => setIsRecommendedAddress(false)}
                options={[
                  {
                    label: '',
                    secondaryLabel: formatAddress(userAddress),
                    value: 'val2',
                  },
                ]}
              />
            </div>
            <div className="buttongrp">
              <Button onClick={handleConfirmAddress} primary>
                {getLocalizedText(
                  'components.addressConfirmationDialog.useThisAddressbtn',
                )}
              </Button>
              <Button
                onClick={() => handleDialogClose('addNewAddress')}
                tertiary
              >
                {getLocalizedText(
                  'components.addressConfirmationDialog.addNewAddressbtn',
                )}
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="headingWrapperams">
              <HeadingText size="sm">
                {getLocalizedText(
                  'components.addressConfirmationDialog.invalidHeadingText',
                )}
              </HeadingText>
            </div>
            <div>{formatAddress(userAddress)}</div>
            <div className="buttongrp">
              <Button
                onClick={() => handleDialogClose('addNewAddress')}
                primary
              >
                {getLocalizedText(
                  'components.addressConfirmationDialog.addNewAddressbtn',
                )}
              </Button>
              <Button onClick={handleConfirmAddress} tertiary>
                {getLocalizedText(
                  'components.addressConfirmationDialog.useThisAddressbtn',
                )}
              </Button>
            </div>
          </>
        )}
      </div>
    </Dialog>
  )
}

export default AddressConfirmationDialog
